import { motion } from "framer-motion";
import { AlertCircle, ArrowLeft, Brain, Lock, Mail } from "../../components/icons";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserAgreementsModal } from "../../components/UserAgreementsModal";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";

export function LoginPageFR() {
  const navigate = useNavigate();
  const { user, checkAgreements } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showAgreements, setShowAgreements] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "" });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (signInError) throw signInError;
      if (!data.user) throw new Error("Echec de la connexion");
    } catch (err: any) {
      if (err.message?.includes("Invalid login credentials")) {
        setError("E-mail ou mot de passe invalide");
      } else if (err.message?.includes("Email not confirmed")) {
        setError("Veuillez confirmer votre adresse e-mail");
      } else {
        setError(err.message || "Impossible de se connecter");
      }
      await supabase.auth.signOut();
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    setResetSuccess(false);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/fr/login`,
      });
      if (resetError) throw resetError;
      setResetSuccess(true);
    } catch (err: any) {
      setError(err.message || "Impossible d'envoyer l'e-mail de reinitialisation");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAgreementsSuccess = async () => {
    if (pendingUserId) {
      navigate("/fr/dashboard");
    }
  };

  const handleUserLoggedIn = async (uid: string) => {
    try {
      setIsLoading(true);
      setPendingUserId(uid);
      const hasAgreements = await checkAgreements(uid);
      if (!hasAgreements) {
        setShowAgreements(true);
        setIsLoading(false);
        return;
      }
      navigate("/fr/dashboard");
    } catch (err) {
      console.error("Error handling user logged in:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        supabase.auth.signOut().then(() => setIsReady(true));
      } else {
        setIsReady(true);
      }
    });
  }, []);

  useEffect(() => {
    if (isReady && user?.id) {
      handleUserLoggedIn(user.id);
    }
  }, [user, isReady]);

  return (
    <div className="min-h-screen freud-bg py-12 px-4">
      <div className="max-w-md mx-auto">
        <Link
          to="/fr"
          className="inline-flex items-center gap-2 text-chocolate-600 hover:text-sage-600 mb-8 transition-colors font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour a l'accueil
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="w-16 h-16 bg-sage-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-soft">
            <Brain className="w-9 h-9 text-white" />
          </div>
          <h1 className="freud-heading-2xl text-chocolate-900 mb-2">
            {showForgotPassword ? "Reinitialiser le mot de passe" : "Bon retour parmi nous"}
          </h1>
          <p className="freud-text-base text-chocolate-500">
            {showForgotPassword
              ? "Entrez votre e-mail pour recevoir les instructions de reinitialisation"
              : "Connectez-vous pour acceder a votre tableau de bord"}
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="freud-card-elevated p-8">
          {error && (
            <div className="flex items-center gap-2 bg-warm-100 text-warm-700 p-4 rounded-xl mb-6">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {resetSuccess && (
            <div className="flex items-center gap-2 bg-sage-100 text-sage-700 p-4 rounded-xl mb-6">
              <Mail className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">E-mail de reinitialisation envoye ! Verifiez votre boite de reception.</p>
            </div>
          )}

          {showForgotPassword ? (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label htmlFor="resetEmail" className="freud-label block text-chocolate-800 mb-2">Adresse e-mail</label>
                <input type="email" id="resetEmail" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} className="freud-input" required disabled={isLoading} placeholder="Entrez votre adresse e-mail" />
              </div>
              <div className="flex justify-between items-center pt-4">
                <button type="button" onClick={() => setShowForgotPassword(false)} className="text-chocolate-600 hover:text-sage-600 transition-colors font-medium">Retour a la connexion</button>
                <button type="submit" disabled={isLoading} className="freud-btn-primary inline-flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  {isLoading ? "Envoi en cours..." : "Envoyer le lien"}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label htmlFor="email" className="freud-label block text-chocolate-800 mb-2">Adresse e-mail</label>
                <input type="email" id="email" value={credentials.email} onChange={(e) => setCredentials((prev) => ({ ...prev, email: e.target.value }))} className="freud-input" required disabled={isLoading} placeholder="Entrez votre adresse e-mail" />
              </div>
              <div>
                <label htmlFor="password" className="freud-label block text-chocolate-800 mb-2">Mot de passe</label>
                <input type="password" id="password" value={credentials.password} onChange={(e) => setCredentials((prev) => ({ ...prev, password: e.target.value }))} className="freud-input" required disabled={isLoading} placeholder="Entrez votre mot de passe" />
              </div>
              <div className="flex justify-between items-center pt-4">
                <button type="button" onClick={() => setShowForgotPassword(true)} className="text-chocolate-600 hover:text-sage-600 transition-colors font-medium text-sm">Mot de passe oublie ?</button>
                <button type="submit" disabled={isLoading} className="freud-btn-primary inline-flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  {isLoading ? "Connexion..." : "Se connecter"}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>

      <UserAgreementsModal
        isOpen={showAgreements}
        onClose={() => setShowAgreements(false)}
        userId={pendingUserId}
        onSuccess={handleAgreementsSuccess}
      />
    </div>
  );
}
